import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTabbedSearch } from '../../../hooks/useTabbedSearch';
import { search } from '../../../services/practice-service';
import { getReaderProfileOnboardingConfig } from '../../../services/configuration-service';

export const useReadingPracticeSearch = () => {
  const draftTab = 0;
  const publishTab = 1;
  const approvedTab = 2;
  const releasedTab = 3;
  const archivedTab = 4;

  const [readingCategories, setReadingCategories] = useState([]);

  // Load static configurations on mount
  useEffect(() => {
    getReaderProfileOnboardingConfig()
      .then(config => {
        if (config && config.readingCategories) {
          setReadingCategories(config.readingCategories);
        }
      })
      .catch(err => console.error("Failed to fetch onboarding configuration:", err));
  }, []);

  const tabsConfig = useMemo(() => [
    { id: draftTab, status: 'DRAFT', initialFilters: { categoryIds: [], gradeLevels: [] } },
    { id: publishTab, status: 'PUBLISHED', initialFilters: { categoryIds: [], gradeLevels: [] } },
    { id: approvedTab, status: 'APPROVED', initialFilters: { categoryIds: [], gradeLevels: [] } },
    { id: releasedTab, status: 'RELEASED', initialFilters: { categoryIds: [], gradeLevels: [] } },
    { id: archivedTab, status: 'ARCHIVED', initialFilters: { categoryIds: [], gradeLevels: [] } },
  ], []);

  // Fetch handler containing query formatting & payload mapping
  const fetchData = useCallback(async ({ status, searchTerm, pageNum, pageSize, filters }) => {
    const { categoryIds = [], gradeLevels = [] } = filters;
    const result = await search({
      term: searchTerm,
      categoryIds,
      gradeLevels,
      pageNumber: pageNum,
      pageSize,
      statuses: [status],
    });

    // Map the response payload as required by reading practices UI
    const items = (result.items || []).map(entry => ({
      ...entry.cover,
      categoryId: entry.cover?.categoryId,
    }));

    return {
      items,
      hasNext: result.hasNext || false,
    };
  }, []);

  const {
    loading,
    error,
    searchTerm,
    filters,
    viewType,
    tabValue,
    showCreateForm,
    hasMore,
    submitSearchParams: genericSubmitSearchParams,
    setShowCreateForm,
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    getTabContents,
    refreshTab,
  } = useTabbedSearch({
    tabs: tabsConfig,
    initialTabValue: publishTab,
    pageSize: 10,
    fetchDataFn: fetchData,
    cacheKey: 'reading-practices',
  });

  // Adapt submitSearchParams to maintain backward compatibility with (term, categories, gradeLevels) arguments
  const submitSearchParams = useCallback((term, categories, gradeLevels) => {
    genericSubmitSearchParams(term, {
      categoryIds: categories || [],
      gradeLevels: gradeLevels || [],
    });
  }, [genericSubmitSearchParams]);

  const handleContentGenerated = useCallback(() => {
    refreshTab(draftTab);
    setShowCreateForm(false);
  }, [refreshTab, setShowCreateForm]);

  return {
    loading,
    error,
    searchTerm,
    selectedCategories: filters?.categoryIds || [],
    selectedGradeLevels: filters?.gradeLevels || [],
    viewType,
    tabValue,
    showCreateForm,
    hasMore,
    submitSearchParams,
    setshowCreateForm: setShowCreateForm, // Keep case-compatibility for the page view
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    handleContentGenerated,
    getTabContents,
    draftTab,
    publishTab,
    approvedTab,
    releasedTab,
    archivedTab,
    readingCategories,
  };
};